import { useState } from 'react'
import { Dialog, DialogPanel, DialogTitle, DialogBackdrop, RadioGroup, Radio, Label, Field } from '@headlessui/react'
import { denySubmission } from '../../pocketbase/PocketbasePendingSubmissionsService'

const DENIAL_REASONS = [
  'Incomplete information',
  'Incorrect or unverifiable data',
  'Duplicate submission',
  'Low-quality image',
  'Image does not match the fragrance',
  'Formatting does not meet guidelines',
  'Contains copyrighted/duplicate text',
  'Inappropriate content',
  'Unable to verify authenticity',
] as const

interface IDenySubmissionDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  submissionId: string | null
}

export const DenySubmissionDialog = (props: IDenySubmissionDialogProps) => {
  const { isOpen, onClose, onSuccess, submissionId } = props

  const [selectedReason, setSelectedReason] = useState<string>('')
  const [customReason, setCustomReason] = useState('')
  const [isOther, setIsOther] = useState(false)
  const [isDenying, setIsDenying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleReasonChange = (value: string) => {
    if (value === 'other') {
      setIsOther(true)
      setSelectedReason('')
    } else {
      setIsOther(false)
      setSelectedReason(value)
      setCustomReason('')
    }
  }

  const handleConfirm = async () => {
    if (!submissionId) return

    const reason = isOther ? customReason.trim() : selectedReason
    if (!reason) return

    try {
      setIsDenying(true)
      setError(null)

      await denySubmission(submissionId, reason)

      // Reset state
      setSelectedReason('')
      setCustomReason('')
      setIsOther(false)

      // Call success callback
      onSuccess()

      // Close dialog
      onClose()
    } catch (error: any) {
      console.error('Error denying submission:', error)

      // Extract error message
      let errorMessage = 'Failed to deny submission. Please try again.'
      if (error?.message) {
        errorMessage = error.message
      } else if (error?.data?.message) {
        errorMessage = error.data.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      setError(errorMessage)
    } finally {
      setIsDenying(false)
    }
  }

  const handleClose = () => {
    if (!isDenying) {
      // Reset state when closing
      setSelectedReason('')
      setCustomReason('')
      setIsOther(false)
      setError(null)
      onClose()
    }
  }

  const isValid = isOther ? customReason.trim().length > 0 : selectedReason.length > 0

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
          <DialogTitle className="text-lg font-semibold text-gray-900">
            Deny Submission
          </DialogTitle>

          <p className="mt-2 text-sm text-gray-600">
            Please select a reason for denying this submission:
          </p>

          <RadioGroup
            value={isOther ? 'other' : selectedReason}
            onChange={handleReasonChange}
            className="mt-4 space-y-2"
          >
            {DENIAL_REASONS.map((reason) => (
              <Field key={reason} className="flex items-center gap-3">
                <Radio
                  value={reason}
                  className="group flex size-5 items-center justify-center rounded-full border border-gray-300 bg-white data-[checked]:border-black data-[checked]:bg-black cursor-pointer"
                >
                  <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
                </Radio>
                <Label className="text-sm text-gray-700 cursor-pointer">{reason}</Label>
              </Field>
            ))}

            {/* Other option */}
            <Field className="flex items-center gap-3">
              <Radio
                value="other"
                className="group flex size-5 items-center justify-center rounded-full border border-gray-300 bg-white data-[checked]:border-black data-[checked]:bg-black cursor-pointer"
              >
                <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
              </Radio>
              <Label className="text-sm text-gray-700 cursor-pointer">Other (custom reason)</Label>
            </Field>
          </RadioGroup>

          {/* Custom reason text area */}
          {isOther && (
            <textarea
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Enter custom reason..."
              className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
              rows={3}
            />
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={handleClose}
              disabled={isDenying}
              className={`px-4 py-2 text-sm font-medium ${isDenying
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:text-gray-900 cursor-pointer'
                }`}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isValid || isDenying}
              className="inline-flex items-center rounded-md bg-black p-3 text-base text-white hover:bg-gray-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDenying ? 'Denying...' : 'Deny Submission'}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
